import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import { clampDiscount, computeOfferPrices, formatMoney, priceFromDiscount, round2 } from '../utils/itemPricing';
import { ensureWebImageStyles, pickWebImage, swalAbove } from '../utils/webImageSearch';

const API = process.env.REACT_APP_API_BASE_URL;

const SERIES_OPTIONS = [
  { value: 'RS', label: 'Regular Stock (RS)' },
  { value: 'AO', label: 'Against Order (AO)' },
  { value: 'SC', label: 'Stock Clearance (SC)' },
  { value: 'DC', label: 'Discontinued (DC)' },
  { value: 'ITF', label: 'Item to Focus (ITF)' },
  { value: 'OT', label: 'One Time (OT)' },
];

const getActiveOfferTypes = (brand) => {
  let ots;
  if (brand?.offerTypes && brand.offerTypes.length) {
    ots = brand.offerTypes.filter((ot) => ot.active !== false);
  } else if (brand?.supportedOfferTypes && brand.supportedOfferTypes.length) {
    ots = brand.supportedOfferTypes.map((key) => ({ key, label: String(key).toUpperCase() }));
  } else {
    // Brand has no configured offer types: only the reserved Flat (= MRP).
    ots = [];
  }
  const withoutFlat = ots.filter((ot) => ot.key !== 'flat');
  const flat = ots.find((ot) => ot.key === 'flat');
  withoutFlat.push(flat || { key: 'flat', label: 'Flat' });
  return withoutFlat;
};

const emptyForm = {
  brand: '',
  category: '',
  skuCode: '',
  skuDescription: '',
  series: 'RS',
  hsnCode: '',
  gstRate: '',
  mrp: '',
  discountPercent: 0,
  offerType: {},
  additionalFieldValues: {},
  isActive: true,
  variants: [],
  subItems: [],
};

const ItemEditModal = ({ isOpen, itemId, initialItem, isDarkMode, user, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [brandAdditionalFields, setBrandAdditionalFields] = useState([]);
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [subItemQuery, setSubItemQuery] = useState('');
  const [subItemOptions, setSubItemOptions] = useState([]);
  const [subItemLoading, setSubItemLoading] = useState(false);
  const [subItemPricing, setSubItemPricing] = useState({});
  const [webSearching, setWebSearching] = useState(false);

  const selectStyles = useMemo(() => ({
    control: (p, s) => ({
      ...p,
      minHeight: 42,
      fontSize: 14,
      borderRadius: 10,
      border: s.isFocused ? '2px solid #6366f1' : `1px solid ${isDarkMode ? '#4b5563' : '#d1d5db'}`,
      backgroundColor: isDarkMode ? '#374151' : '#ffffff',
      boxShadow: 'none',
    }),
    singleValue: (p) => ({ ...p, color: isDarkMode ? '#f3f4f6' : '#111827' }),
    input: (p) => ({ ...p, color: isDarkMode ? '#f3f4f6' : '#111827' }),
    placeholder: (p) => ({ ...p, color: isDarkMode ? '#9ca3af' : '#6b7280' }),
    menu: (p) => ({
      ...p,
      zIndex: 99999,
      backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
      border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}`,
    }),
    menuPortal: (p) => ({ ...p, zIndex: 99999 }),
    menuList: (p) => ({ ...p, backgroundColor: isDarkMode ? '#1f2937' : '#ffffff' }),
    option: (p, s) => ({
      ...p,
      fontSize: 14,
      backgroundColor: s.isSelected ? '#6366f1' : s.isFocused ? (isDarkMode ? '#374151' : '#eef2ff') : (isDarkMode ? '#1f2937' : '#ffffff'),
      color: s.isSelected ? '#ffffff' : (isDarkMode ? '#e5e7eb' : '#111827'),
    }),
    multiValue: (p) => ({ ...p, backgroundColor: isDarkMode ? '#4b5563' : '#e0e7ff' }),
    multiValueLabel: (p) => ({ ...p, color: isDarkMode ? '#f3f4f6' : '#3730a3' }),
    multiValueRemove: (p) => ({ ...p, color: isDarkMode ? '#cbd5e1' : '#4338ca' }),
  }), [isDarkMode]);

  const fieldBase = `w-full px-3 py-2.5 border rounded-lg text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
    isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  }`;
  const labelBase = `block text-sm font-semibold mb-1.5 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`;
  const smallLabel = `block text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`;

  const hydrateSubItemPricing = useCallback(async (items) => {
    const ids = [...new Set((items || [])
      .map((s) => (typeof s.item === 'object' ? s.item?._id : s.item))
      .filter(Boolean)
      .map(String))];
    if (!ids.length) {
      setSubItemPricing({});
      return;
    }
    const entries = await Promise.all(ids.map(async (id) => {
      try {
        const res = await axios.get(`${API}/items/id/${id}`);
        return [id, { mrp: Number(res.data?.mrp) || 0, offerType: res.data?.offerType || {} }];
      } catch (_) {
        return [id, { mrp: null, offerType: {} }];
      }
    }));
    setSubItemPricing((prev) => {
      const next = { ...prev };
      entries.forEach(([id, pricing]) => { next[id] = pricing; });
      return next;
    });
  }, []);

  useEffect(() => {
    ensureWebImageStyles();
    return undefined;
  }, []);

  const load = useCallback(async () => {
    if (!isOpen || !itemId) return;
    setLoading(true);
    setSubItemQuery('');
    setSubItemOptions([]);
    setSubItemPricing({});
    setImageFile(null);
    try {
      const token = localStorage.getItem('token');
      const [brandsRes, itemRes] = await Promise.all([
        axios.get(`${API}/brands/all`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/items/id/${itemId}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const brandOptions = (brandsRes.data?.brands || []).map((b) => ({
        value: b._id,
        label: b.name,
        categories: b.categories || [],
        additionalFields: b.additionalFields || [],
        offerTypes: b.offerTypes || [],
        supportedOfferTypes: b.supportedOfferTypes || [],
        advancedItems: !!b.advancedItems,
        accessoriesEnabled: !!b.advancedItems && !!b.accessoriesEnabled,
      }));
      setBrands(brandOptions);

      const item = itemRes.data || initialItem || {};
      const brandOpt = brandOptions.find((b) => String(b.value) === String(item.brand?._id || item.brand)) || null;
      setSelectedBrand(brandOpt);
      setBrandAdditionalFields(brandOpt?.additionalFields || []);

      const categoryId = item.category?._id || item.category;
      const catOpt = (brandOpt?.categories || []).find((c) => String(c._id) === String(categoryId));
      setSelectedCategory(catOpt ? { value: catOpt._id, label: catOpt.name } : null);

      const mappedSubItems = (item.subItems || []).map((s) => ({
        item: {
          _id: s.item?._id || s.item,
          skuCode: s.item?.skuCode || '',
          skuDescription: s.item?.skuDescription || '',
          variants: (s.item?.variants || []).map((v) => v.name).filter(Boolean),
        },
        quantity: s.quantity || 1,
        variantName: s.variantName || '',
      }));

      setForm({
        brand: item.brand?._id || item.brand || '',
        category: categoryId || '',
        skuCode: item.skuCode || '',
        skuDescription: item.skuDescription || '',
        series: item.series || 'RS',
        hsnCode: item.hsnCode || '',
        gstRate: item.gstRate ?? '',
        mrp: item.mrp ?? '',
        discountPercent: item.discountPercent ?? 0,
        offerType: { ...(item.offerType || {}) },
        additionalFieldValues: { ...(item.additionalFieldValues || {}) },
        isActive: item.isActive !== false,
        variants: (item.variants || []).map((v) => ({
          variantId: v.variantId || '',
          name: v.name || '',
          mrp: v.mrp ?? '',
          discountPercent: v.discountPercent ?? 0,
          offerType: { ...(v.offerType || {}) },
          additionalFieldValues: { ...(v.additionalFieldValues || {}) },
          imageId: v.imageId || null,
          preferredSubItems: (v.preferredSubItems || []).map((s) => (typeof s === 'object' ? s._id : s)),
          isDefault: !!v.isDefault,
          isActive: v.isActive !== false,
        })),
        subItems: mappedSubItems,
      });

      hydrateSubItemPricing(mappedSubItems);

      setImagePreview(item._id ? `${API}/items/image/${item._id}?t=${Date.now()}` : '');
      setImageFile(null);
    } catch (err) {
      console.error('Failed to load item for editing:', err);
      swalAbove('Error', err.response?.data?.error || 'Failed to load item details', 'error');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [isOpen, itemId, initialItem, onClose, hydrateSubItemPricing]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, itemId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !Swal.isVisible()) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, saving, onClose]);

  const handleBrandChange = (opt) => {
    setSelectedBrand(opt);
    setSelectedCategory(null);
    setBrandAdditionalFields(opt?.additionalFields || []);
    const additional = {};
    (opt?.additionalFields || []).forEach((f) => {
      additional[f.fieldName] = f.defaultValue || '';
    });
    const nextBrand = brands.find((b) => b.value === opt?.value);
    const keys = getActiveOfferTypes(nextBrand).filter((ot) => ot.key !== 'flat').map((ot) => ot.key);
    const remap = (existing, mrp, discountPercent) => {
      const next = {};
      keys.forEach((key) => {
        const hasValue = existing && Object.prototype.hasOwnProperty.call(existing, key)
          && existing[key] !== '' && existing[key] !== null && existing[key] !== undefined;
        next[key] = hasValue ? existing[key] : (Number(mrp) ? priceFromDiscount(mrp, discountPercent) : '');
      });
      return next;
    };
    setForm((p) => ({
      ...p,
      brand: opt?.value || '',
      category: '',
      additionalFieldValues: additional,
      offerType: remap(p.offerType, p.mrp, p.discountPercent),
      variants: (p.variants || []).map((v) => ({
        ...v,
        offerType: remap(v.offerType, v.mrp, v.discountPercent),
      })),
    }));
  };

  const handleCategoryChange = (opt) => {
    setSelectedCategory(opt);
    setForm((p) => ({ ...p, category: opt?.value || '' }));
  };

  const setAdditionalValue = (name, value) => {
    setForm((p) => ({ ...p, additionalFieldValues: { ...p.additionalFieldValues, [name]: value } }));
  };

  const activeOfferTypes = getActiveOfferTypes(brands.find((b) => b.value === form.brand));

  const recalcParentPrices = (patch = {}) => {
    setForm((p) => {
      const mrp = patch.mrp !== undefined ? patch.mrp : p.mrp;
      const discountPercent = patch.discountPercent !== undefined ? patch.discountPercent : p.discountPercent;
      const keys = getActiveOfferTypes(brands.find((b) => b.value === p.brand)).map((o) => o.key);
      const prices = computeOfferPrices(mrp, discountPercent, keys);
      return { ...p, ...patch, offerType: { ...p.offerType, ...prices } };
    });
  };

  const recalcVariantPrices = (index, patch = {}) => {
    setForm((p) => ({
      ...p,
      variants: (p.variants || []).map((v, i) => {
        if (i !== index) return v;
        const mrp = patch.mrp !== undefined ? patch.mrp : v.mrp;
        const discountPercent = patch.discountPercent !== undefined ? patch.discountPercent : v.discountPercent;
        const keys = getActiveOfferTypes(brands.find((b) => b.value === p.brand)).map((o) => o.key);
        const prices = computeOfferPrices(mrp, discountPercent, keys);
        return { ...v, ...patch, offerType: { ...v.offerType, ...prices } };
      }),
    }));
  };

  const handleWebImageSearch = async () => {
    if (webSearching) return;
    setWebSearching(true);
    try {
      const file = await pickWebImage({
        defaultQuery: [form.skuDescription, form.skuCode].filter(Boolean).join(' ').trim(),
        isDarkMode,
      });
      if (file) {
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
        toast.success('Web image attached');
      }
    } finally {
      setWebSearching(false);
    }
  };
  const searchSubItems = async (value) => {
    setSubItemQuery(value);
    if (!value || value.trim().length < 2) { setSubItemOptions([]); return; }
    setSubItemLoading(true);
    try {
      const res = await axios.get(`${API}/items`, { params: { filter: value, limit: 20, page: 0 } });
      setSubItemOptions((res.data || []).filter((it) => String(it._id) !== String(itemId)).map((it) => ({
        value: it._id,
        label: `${it.skuCode}${it.skuDescription ? ` - ${it.skuDescription}` : ''}`,
        item: it,
      })));
    } catch (e) {
      setSubItemOptions([]);
    } finally {
      setSubItemLoading(false);
    }
  };

  const addSubItem = (opt) => {
    if (!opt?.item) return;
    if ((form.subItems || []).some((s) => String(s.item._id) === String(opt.item._id))) {
      toast.info('That item is already a sub-item.');
      return;
    }
    setForm((p) => ({
      ...p,
      subItems: [...(p.subItems || []), {
        item: {
          _id: opt.item._id,
          skuCode: opt.item.skuCode,
          skuDescription: opt.item.skuDescription,
        },
        quantity: 1,
        variantName: '',
      }],
    }));
    setSubItemQuery('');
    setSubItemOptions([]);
    const id = String(opt.item._id);
    setSubItemPricing((prev) => ({
      ...prev,
      [id]: { mrp: opt.item.mrp != null ? Number(opt.item.mrp) : null, offerType: opt.item.offerType || {} },
    }));
    hydrateSubItemPricing([{ item: { _id: id } }]);
  };

  const updateSubItem = (index, patch) => {
    setForm((p) => ({
      ...p,
      subItems: (p.subItems || []).map((s, i) => (i === index ? { ...s, ...patch } : s)),
    }));
  };

  const addVariant = () => {
    setForm((p) => ({
      ...p,
      variants: [...(p.variants || []), {
        variantId: '', name: '', mrp: '', discountPercent: 0, offerType: {}, additionalFieldValues: {},
        imageId: null, preferredSubItems: [], isDefault: false, isActive: true,
      }],
    }));
  };

  const updateVariant = (index, patch) => {
    setForm((p) => ({
      ...p,
      variants: (p.variants || []).map((v, i) => (i === index ? { ...v, ...patch } : v)),
    }));
  };

  const validate = () => {
    if (!form.brand) return 'Brand is required.';
    if (!form.category) return 'Category is required.';
    if (!String(form.skuCode).trim()) return 'SKU Code is required.';
    if (!String(form.skuDescription).trim()) return 'SKU Description is required.';
    if (!form.series) return 'Series is required.';
    if (!String(form.hsnCode).trim()) return 'HSN Code is required.';
    if (form.gstRate === '' || Number.isNaN(Number(form.gstRate))) return 'GST Rate is required.';
    if (form.mrp === '' || Number.isNaN(Number(form.mrp))) return 'MRP is required.';
    const missing = (brandAdditionalFields || []).find(
      (f) => f.isRequired && !String(form.additionalFieldValues?.[f.fieldName] || '').trim()
    );
    if (missing) return `${missing.fieldName} is required.`;
    return '';
  };

  const handleSave = async () => {
    const error = validate();
    if (error) { swalAbove('Incomplete', error, 'warning'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('brand', form.brand);
      fd.append('category', form.category);
      fd.append('skuCode', form.skuCode);
      fd.append('skuDescription', form.skuDescription);
      fd.append('series', form.series);
      fd.append('hsnCode', form.hsnCode);
      fd.append('gstRate', String(form.gstRate));
      fd.append('mrp', String(form.mrp));
      fd.append('discountPercent', String(clampDiscount(form.discountPercent)));
      fd.append('offerType', JSON.stringify({ ...form.offerType, flat: form.mrp }));
      fd.append('additionalFieldValues', JSON.stringify(form.additionalFieldValues || {}));
      fd.append('isActive', String(form.isActive !== false));
      if (user?.user?._id) fd.append('updatedBy', user.user._id);
      fd.append('subItems', JSON.stringify((form.subItems || []).map((s) => ({
        item: s.item?._id || s.item,
        quantity: Number(s.quantity) || 1,
        variantName: s.variantName || '',
      }))));
      fd.append('variants', JSON.stringify((form.variants || []).map((v) => ({
        variantId: v.variantId || '',
        name: v.name || '',
        mrp: Number(v.mrp) || 0,
        discountPercent: clampDiscount(v.discountPercent),
        offerType: v.offerType || {},
        additionalFieldValues: v.additionalFieldValues || {},
        imageId: v.imageId || undefined,
        preferredSubItems: (v.preferredSubItems || []).filter(Boolean),
        isDefault: !!v.isDefault,
        isActive: v.isActive !== false,
      }))));
      if (imageFile) fd.append('image', imageFile);

      await axios.put(`${API}/items/${itemId}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });

      const fresh = await axios.get(`${API}/items/id/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Item updated');
      onSaved(fresh.data);
      onClose();
    } catch (err) {
      console.error('Item update failed:', err);
      swalAbove('Error', err.response?.data?.error || err.response?.data?.details || 'Failed to update item', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const baseFlat = Number(form.mrp) || 0;
  const accessoriesTotal = round2((form.subItems || []).reduce((sum, s) => {
    const id = String(s.item?._id || s.item || '');
    const unit = Number(subItemPricing[id]?.mrp) || 0;
    return sum + unit * (Number(s.quantity) || 1);
  }, 0));
  const grandTotal = round2(baseFlat + accessoriesTotal);
  const showAccessories = !!(selectedBrand?.accessoriesEnabled || (form.subItems || []).length > 0);

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4">
      <div className={`flex flex-col w-full max-w-5xl max-h-[94vh] rounded-2xl border shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-start justify-between gap-4 px-5 sm:px-6 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 flex-shrink-0">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-white">Edit Item</h3>
            <p className="text-indigo-100 text-xs sm:text-sm mt-0.5">Changes update the item master and all quotation rows using it</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-colors disabled:opacity-50" aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-5 sm:px-6 py-5">
          {loading ? (
            <div className="py-24 text-center">
              <div className="w-12 h-12 mx-auto border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              <p className={`mt-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading item...</p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className={labelBase}>Brand <span className="text-red-500">*</span></label>
                    <Select value={selectedBrand} onChange={handleBrandChange} options={brands} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" placeholder="Select brand..." />
                  </div>
                  <div>
                    <label className={labelBase}>Category <span className="text-red-500">*</span></label>
                    <Select
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      options={(selectedBrand?.categories || []).map((c) => ({ value: c._id, label: c.name }))}
                      isDisabled={!selectedBrand}
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      placeholder={selectedBrand ? 'Select category...' : 'Select brand first'}
                    />
                  </div>
                  <div>
                    <label className={labelBase}>SKU Code <span className="text-red-500">*</span></label>
                    <input value={form.skuCode} onChange={(e) => setForm((p) => ({ ...p, skuCode: e.target.value }))} className={fieldBase} placeholder="SKU code" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelBase}>SKU Description <span className="text-red-500">*</span></label>
                    <input value={form.skuDescription} onChange={(e) => setForm((p) => ({ ...p, skuDescription: e.target.value }))} className={fieldBase} placeholder="Description" />
                  </div>
                  <div>
                    <label className={labelBase}>Series <span className="text-red-500">*</span></label>
                    <Select value={SERIES_OPTIONS.find((s) => s.value === form.series) || null} onChange={(o) => setForm((p) => ({ ...p, series: o?.value || 'RS' }))} options={SERIES_OPTIONS} styles={selectStyles} menuPortalTarget={document.body} menuPosition="fixed" />
                  </div>
                  <div>
                    <label className={labelBase}>HSN Code <span className="text-red-500">*</span></label>
                    <input value={form.hsnCode} onChange={(e) => setForm((p) => ({ ...p, hsnCode: e.target.value }))} className={fieldBase} placeholder="HSN code" />
                  </div>
                  <div>
                    <label className={labelBase}>GST Rate (%) <span className="text-red-500">*</span></label>
                    <input type="number" step="0.01" min="0" max="100" value={form.gstRate} onChange={(e) => setForm((p) => ({ ...p, gstRate: e.target.value }))} className={fieldBase} />
                  </div>
                  <div>
                    <label className={labelBase}>Status</label>
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, isActive: !p.isActive }))}
                      className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm font-semibold ${
                        form.isActive
                          ? (isDarkMode ? 'bg-emerald-900/30 border-emerald-700 text-emerald-300' : 'bg-emerald-50 border-emerald-300 text-emerald-700')
                          : (isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-600')
                      }`}
                    >
                      {form.isActive ? 'Active' : 'Inactive'}
                      <span className={`w-2 h-2 rounded-full ${form.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                    </button>
                  </div>
                </div>
              </div>

              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Product Image</h4>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleWebImageSearch}
                      disabled={webSearching}
                      className={`text-xs px-3 py-1.5 rounded-lg font-semibold border transition-colors disabled:opacity-60 ${isDarkMode ? 'bg-indigo-900/30 border-indigo-600/50 text-indigo-300 hover:bg-indigo-900/50' : 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100'}`}
                    >
                      {webSearching ? 'Searching...' : 'Auto-Find on Web'}
                    </button>
                    <label className={`text-xs px-3 py-1.5 rounded-lg font-semibold cursor-pointer ${isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-100 text-indigo-700'}`}>
                      Upload image
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (!file.type?.startsWith('image/') || file.size > 5 * 1024 * 1024) {
                            swalAbove('Warning', 'Choose an image under 5MB.', 'warning');
                            e.target.value = '';
                            return;
                          }
                          setImageFile(file);
                          setImagePreview(URL.createObjectURL(file));
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                </div>
                {imagePreview ? (
                  <div className="flex items-center gap-3">
                    <img src={imagePreview} alt="Item" className="w-24 h-24 object-cover rounded-lg border border-gray-300" />
                    {imageFile && (
                      <button type="button" onClick={() => { setImageFile(null); setImagePreview(itemId ? `${API}/items/image/${itemId}` : ''); }} className="text-xs text-red-500 font-semibold">
                        Undo new image
                      </button>
                    )}
                  </div>
                ) : (
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No image</p>
                )}
              </div>

              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Pricing</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  <div>
                    <label className={labelBase}>MRP (₹) <span className="text-red-500">*</span></label>
                    <input type="number" step="0.01" min="0" value={form.mrp} onChange={(e) => recalcParentPrices({ mrp: e.target.value })} className={fieldBase} />
                  </div>
                  <div>
                    <label className={labelBase}>Discount %</label>
                    <input type="number" step="0.01" min="0" max="100" value={form.discountPercent ?? 0} onChange={(e) => recalcParentPrices({ discountPercent: e.target.value })} className={fieldBase} />
                  </div>
                  {activeOfferTypes.filter((ot) => ot.key !== 'flat').map((ot) => (
                    <div key={ot.key}>
                      <label className={smallLabel}>{ot.label} (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.offerType?.[ot.key] ?? ''}
                        onChange={(e) => setForm((p) => ({ ...p, offerType: { ...p.offerType, [ot.key]: e.target.value } }))}
                        className={fieldBase}
                        placeholder={ot.label}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <button
                    type="button"
                    onClick={() => recalcParentPrices()}
                    className={`text-xs px-3 py-1.5 rounded-lg font-semibold border ${isDarkMode ? 'border-indigo-600/50 text-indigo-300 hover:bg-indigo-900/30' : 'border-indigo-200 text-indigo-700 hover:bg-indigo-50'}`}
                  >
                    Recalculate prices
                  </button>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Offer price = MRP × (1 − discount%). Flat is always MRP.</p>
                </div>
              </div>

              {brandAdditionalFields.filter((f) => !f.derived && (f.scope || 'both') !== 'accessory').length > 0 && (
                <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Additional Fields</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {brandAdditionalFields.filter((f) => !f.derived && (f.scope || 'both') !== 'accessory').map((f) => (
                      <div key={f.fieldName}>
                        <label className={smallLabel}>{f.fieldName}{f.isRequired && <span className="text-red-500 ml-1">*</span>}</label>
                        {f.fieldType === 'select' ? (
                          <select value={form.additionalFieldValues?.[f.fieldName] || ''} onChange={(e) => setAdditionalValue(f.fieldName, e.target.value)} className={fieldBase}>
                            <option value="">Select {f.fieldName}</option>
                            {(f.options || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                          </select>
                        ) : (
                          <input
                            type={f.fieldType === 'number' ? 'number' : f.fieldType === 'date' ? 'date' : 'text'}
                            min={f.min ?? undefined}
                            max={f.max ?? undefined}
                            maxLength={f.maxLength || undefined}
                            value={form.additionalFieldValues?.[f.fieldName] ?? ''}
                            onChange={(e) => setAdditionalValue(f.fieldName, e.target.value)}
                            className={fieldBase}
                            placeholder={f.fieldName}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(selectedBrand?.advancedItems || (form.variants || []).length > 0) && (
              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Variants</h4>
                  <button type="button" onClick={addVariant} className="px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold">+ Add Variant</button>
                </div>
                {(form.variants || []).length === 0 ? (
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No variants.</p>
                ) : (
                  <div className="space-y-3">
                    {(form.variants || []).map((v, index) => (
                      <div key={index} className={`rounded-lg border p-3 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-xs font-mono ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.variantId || 'New variant'}</span>
                          <button type="button" onClick={() => setForm((p) => ({ ...p, variants: p.variants.filter((_, i) => i !== index) }))} className="text-xs font-semibold text-red-500">Remove</button>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                          <div>
                            <label className={smallLabel}>Name</label>
                            <input value={v.name} onChange={(e) => updateVariant(index, { name: e.target.value })} className={fieldBase} placeholder="Variant name" />
                          </div>
                          <div>
                            <label className={smallLabel}>MRP (₹)</label>
                            <input type="number" step="0.01" value={v.mrp} onChange={(e) => recalcVariantPrices(index, { mrp: e.target.value })} className={fieldBase} />
                          </div>
                          <div>
                            <label className={smallLabel}>Discount %</label>
                            <input type="number" step="0.01" min="0" max="100" value={v.discountPercent ?? 0} onChange={(e) => recalcVariantPrices(index, { discountPercent: e.target.value })} className={fieldBase} />
                          </div>
                          {activeOfferTypes.filter((ot) => ot.key !== 'flat').map((ot) => (
                            <div key={ot.key}>
                              <label className={smallLabel}>{ot.label} (₹)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={v.offerType?.[ot.key] ?? ''}
                                onChange={(e) => updateVariant(index, { offerType: { ...v.offerType, [ot.key]: e.target.value } })}
                                className={fieldBase}
                              />
                            </div>
                          ))}
                        </div>
                        <div className={`mt-2 flex flex-wrap items-center gap-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span>Flat/MRP: <strong className={isDarkMode ? 'text-gray-200' : 'text-gray-800'}>₹{formatMoney(v.mrp)}</strong></span>
                          <button
                            type="button"
                            onClick={() => recalcVariantPrices(index)}
                            className={`px-2 py-1 rounded border font-semibold ${isDarkMode ? 'border-violet-600/50 text-violet-300 hover:bg-violet-900/30' : 'border-violet-200 text-violet-700 hover:bg-violet-50'}`}
                          >
                            Recalculate
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {(selectedBrand?.accessoriesEnabled || (form.subItems || []).length > 0) && (
              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-900/40 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Sub-items / Add-ons</h4>
                <div className="mb-3">
                  <Select
                    value={null}
                    inputValue={subItemQuery}
                    onInputChange={(val, meta) => { if (meta.action === 'input-change') searchSubItems(val); }}
                    onChange={addSubItem}
                    options={subItemOptions}
                    isLoading={subItemLoading}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    placeholder="Search items to add..."
                    isClearable
                  />
                </div>
                {(form.subItems || []).length === 0 ? (
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No sub-items.</p>
                ) : (
                  <div className="space-y-2">
                    {(form.subItems || []).map((s, index) => {
                      const pricing = subItemPricing[String(s.item?._id || s.item || '')] || {};
                      const hasPrice = pricing.mrp !== null && pricing.mrp !== undefined;
                      const unit = hasPrice ? Number(pricing.mrp) || 0 : 0;
                      const lineTotal = round2(unit * (Number(s.quantity) || 1));
                      return (
                        <div key={index} className={`flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border p-2.5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                          <div className="flex-1 min-w-0 text-sm">
                            <div className={`font-semibold truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{s.item?.skuCode}</div>
                            <div className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{s.item?.skuDescription}</div>
                            <div className={`text-xs mt-0.5 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                              {hasPrice
                                ? `Master MRP ₹${formatMoney(unit)} · Line ₹${formatMoney(lineTotal)}`
                                : 'Master price unavailable'}
                            </div>
                          </div>
                          <input type="number" min="1" value={s.quantity} onChange={(e) => updateSubItem(index, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })} className="w-20 px-2 py-1.5 border rounded-lg text-sm" />
                          <input value={s.variantName || ''} onChange={(e) => updateSubItem(index, { variantName: e.target.value })} className="w-32 px-2 py-1.5 border rounded-lg text-sm" placeholder="Variant (optional)" />
                          <button type="button" onClick={() => setForm((p) => ({ ...p, subItems: p.subItems.filter((_, i) => i !== index) }))} className="text-xs font-semibold text-red-500 px-2">Remove</button>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(form.subItems || []).length > 0 && (
                  <div className={`mt-3 flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Accessories total (flat/MRP)</span>
                    <span className={`font-bold ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>₹{formatMoney(accessoriesTotal)}</span>
                  </div>
                )}
              </div>
              )}

              {/* Price summary: base + accessories */}
              <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-gray-900/50 border-indigo-700/40' : 'bg-indigo-50/50 border-indigo-200'}`}>
                <h4 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Price Summary</h4>
                <div className="space-y-1.5 text-sm">
                  <div className="flex items-center justify-between">
                    <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Base item (flat/MRP)</span>
                    <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>₹{formatMoney(baseFlat)}</span>
                  </div>
                  {showAccessories && (
                    <div className="flex items-center justify-between">
                      <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>Accessories</span>
                      <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>₹{formatMoney(accessoriesTotal)}</span>
                    </div>
                  )}
                  <div className={`flex items-center justify-between pt-2 mt-1 border-t ${isDarkMode ? 'border-indigo-700/40' : 'border-indigo-200'}`}>
                    <span className={`font-semibold ${isDarkMode ? 'text-indigo-200' : 'text-indigo-800'}`}>Grand total</span>
                    <span className={`text-lg font-bold ${isDarkMode ? 'text-indigo-200' : 'text-indigo-700'}`}>₹{formatMoney(grandTotal)}</span>
                  </div>
                </div>
                {(selectedBrand?.advancedItems || (form.variants || []).length > 0) && (
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Variants are alternative prices and are not added to the grand total.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`flex items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <button type="button" onClick={onClose} disabled={saving} className={`px-4 py-2.5 rounded-lg text-sm font-semibold border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving || loading} className="px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60">
            {saving ? 'Updating...' : 'Update Item'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemEditModal;

